import { SearchIcon } from "@chakra-ui/icons";
import {
  Box,
  Divider,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";
import { Facility } from "@metriport/api-sdk/medical/models/facility";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { getCohorts, Cohort } from "../../../api/cohort";
import { TcmEncounter, TcmEncounterResponse } from "../../../api/tcm-encounter";
import { handleTimeoutOrNetworkErrorWithTryAgainToast } from "../../error/utils";
import { csvColumn, useCSVExport } from "../../../hooks/use-csv-export";
import { useAnalyticsContext, Actions, Features } from "../shared-logic/analytics-context";
import { SidePanel } from "../shared-components/side-panel/side-panel";
import { useMetriportApi } from "../shared-logic/useMetriportApi";
import useMetriportToast from "../shared-logic/useMetriportToast";
import { Button } from "../shared-components/button";
import { METRIPORT_PRIMARY } from "../shared-logic/style";
import { GET_TCM_ENCOUNTER_DEFAULT_COUNT } from "./api";
import FilterBar, {
  ClassOption,
  CodingOption,
  CohortOption,
  DaysOption,
  FacilityOption,
  GetTcmEncounterQueryParams,
  getSortQueryParams,
  StatusOption,
  TocQueryState,
  TocSortState,
  TypeOption,
} from "./filters";
import { buildInitialFilterState, tocFilterReducer } from "./reducer";
import TransitionsOfCareGrid, { TransitionsOfCareGridRef } from "./grid";
import { createUpdatedEncounterData, OutreachAction } from "./outreach-actions";
import { useTcmMutations } from "./tcm-mutations";
import { useTcmApi } from "./useTcmApi";
import { EncounterDetails } from "../encounter/encounter-details";

const enterKeys = ["Enter", "NumpadEnter"];

export type TransitionsOfCareContentProps = {
  onNavigateToPatient?: (patientId: string) => void;
  getPatientUrl?: (patientId: string) => string;
  initialQueryState?: TocQueryState;
  onQueryStateChange?: (queryState: TocQueryState) => void;
  isImpersonating?: boolean;
  capture?: {
    error: (msg: string, options?: { extra?: Record<string, unknown> }) => void;
  };
};

export function TransitionsOfCareContent({
  onNavigateToPatient,
  getPatientUrl,
  initialQueryState,
  onQueryStateChange,
  isImpersonating = false,
  capture,
}: TransitionsOfCareContentProps) {
  const tcmApi = useTcmApi();
  const { onSummaryFilePathChange, updateNoteMutation, updateOutreachLogMutation } =
    useTcmMutations(tcmApi);
  const toast = useMetriportToast();

  const [isLoading, setIsLoading] = useState(false);
  const [totalCountOfTcmEncounters, setTotalCountOfTcmEncounters] = useState<number | undefined>(
    undefined
  );
  const [tableFromTop, setTableFromTopHeight] = useState(0);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | undefined>(
    () => initialQueryState?.encounterId
  );

  const [filters, dispatch] = useReducer(
    tocFilterReducer,
    initialQueryState,
    buildInitialFilterState
  );

  const tcmFilters = useMemo<GetTcmEncounterQueryParams>(() => {
    return {
      ...(filters.facility ? { facility: filters.facility } : {}),
      ...(filters.cohort ? { cohortId: filters.cohort } : {}),
      ...(filters.type ? { type: filters.type } : {}),
      ...(filters.days ? { days: filters.days } : {}),
      ...(filters.coding ? { coding: filters.coding } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.class.length > 0 ? { class: filters.class } : {}),
      ...(filters.appliedSearch ? { search: filters.appliedSearch } : {}),
      ...getSortQueryParams(filters.sort),
    };
  }, [filters]);

  const createFilters = useCallback(
    (overrides?: Partial<GetTcmEncounterQueryParams>): GetTcmEncounterQueryParams => {
      return {
        ...tcmFilters,
        ...overrides,
      };
    },
    [tcmFilters]
  );

  const handleFacilitySelect = useCallback(
    (facility: FacilityOption | undefined) => {
      dispatch({ type: "setFacility", value: facility });
      gridRef?.current?.resetSearch(createFilters({ facility }));
    },
    [createFilters]
  );

  const handleCohortSelect = useCallback(
    (cohort: CohortOption) => {
      dispatch({ type: "setCohort", value: cohort });
      gridRef?.current?.resetSearch(createFilters({ cohortId: cohort }));
    },
    [createFilters]
  );

  const handleTypeSelect = useCallback(
    (type: TypeOption) => {
      dispatch({ type: "setType", value: type });
      gridRef?.current?.resetSearch(createFilters({ type }));
    },
    [createFilters]
  );

  const handleDaySelect = useCallback(
    (days: DaysOption) => {
      dispatch({ type: "setDays", value: days });
      gridRef?.current?.resetSearch(createFilters({ days }));
    },
    [createFilters]
  );

  const handleCodingSelect = useCallback(
    (coding: CodingOption) => {
      dispatch({ type: "setCoding", value: coding });
      gridRef?.current?.resetSearch(createFilters({ coding }));
    },
    [createFilters]
  );

  const handleStatusSelect = useCallback(
    (status: StatusOption) => {
      dispatch({ type: "setStatus", value: status });
      gridRef?.current?.resetSearch(createFilters({ status }));
    },
    [createFilters]
  );

  const handleClassSelect = useCallback(
    (classData: ClassOption) => {
      dispatch({ type: "setClass", value: classData });
      gridRef?.current?.resetSearch(createFilters({ class: classData }));
    },
    [createFilters]
  );

  const [selectedRow, setSelectedRow] = useState<TcmEncounter | null>(null);

  const Analytics = useAnalyticsContext();
  const metriportApi = useMetriportApi();
  const gridRef = useRef<TransitionsOfCareGridRef | null>(null);

  // Debounce ref for note updates
  const debounceRef = useRef<NodeJS.Timeout>();

  const facilitiesQuery = useQuery<Facility[], Error>({
    queryKey: ["facilities"],
    queryFn: () => metriportApi.listFacilities(),
  });

  const cohortsQuery = useQuery<Cohort[], Error>({
    queryKey: ["cohorts"],
    queryFn: () => getCohorts(),
  });

  const effectiveQueryState = useMemo<TocQueryState>(() => {
    return {
      ...(filters.facility ? { facility: filters.facility } : {}),
      ...(filters.cohort ? { cohort: filters.cohort } : {}),
      ...(filters.type ? { type: filters.type } : {}),
      ...(filters.days ? { days: filters.days } : {}),
      ...(filters.coding ? { coding: filters.coding } : {}),
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.class.length > 0 ? { class: filters.class } : {}),
      ...(filters.appliedSearch ? { search: filters.appliedSearch } : {}),
      ...(selectedEncounterId ? { encounterId: selectedEncounterId } : {}),
      ...(filters.sort?.sortBy && filters.sort.sortDir
        ? { sortBy: filters.sort.sortBy, sortDir: filters.sort.sortDir }
        : {}),
    };
  }, [filters, selectedEncounterId]);

  const getTcmEncountersWithQueryParams = useCallback(
    async (
      pageUrl?: string | undefined,
      localQueryParams?: GetTcmEncounterQueryParams | undefined
    ): Promise<TcmEncounterResponse | undefined> => {
      try {
        setIsLoading(true);
        const response = await tcmApi.getTcmEncounters(pageUrl, localQueryParams);
        if (response.meta?.itemsInTotal != undefined)
          setTotalCountOfTcmEncounters(response.meta.itemsInTotal);
        return response;
      } catch (error) {
        console.error("Error retrieving TCM encounters:", error);
        const { handled } = handleTimeoutOrNetworkErrorWithTryAgainToast(error, toast);
        if (!handled) {
          const msg = "Error retrieving TCM encounters";
          capture?.error(msg, {
            extra: { context: `tcm-encounters.get`, error },
          });
          toast.error({ title: msg });
        }
        return undefined;
      } finally {
        setIsLoading(false);
      }
    },
    [tcmApi, toast, capture]
  );

  const handleNoteUpdate = useCallback(
    (encounterId: string, note: string, isUpdateGridNote: boolean) => {
      // Sync grid note with panel
      const rowNode = gridRef.current?.api?.getRowNode(encounterId);
      if (rowNode && isUpdateGridNote) {
        rowNode.setDataValue("freetextNote", note);
      }

      // Clear existing timeout
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        updateNoteMutation.mutate({ encounterId, note });
      }, 1000);
    },
    [updateNoteMutation]
  );

  const handleOutreachLogUpdate = useCallback(
    (status: "Attempted" | "Completed", action: OutreachAction) => {
      if (!selectedRow || !selectedRow.id) return;

      const rowNode = gridRef.current?.api?.getRowNode(selectedRow.id);
      if (!rowNode) return;

      const updatedData = createUpdatedEncounterData(selectedRow, status, action);
      rowNode.setData(updatedData);
      setSelectedRow(updatedData);

      updateOutreachLogMutation.mutate({
        encounterId: selectedRow.id,
        encounter: updatedData,
      });
    },
    [selectedRow, updateOutreachLogMutation]
  );

  const { exportToCSV: exportCSV } = useCSVExport<TcmEncounter>({
    baseFilename: "tcm-encounters",
  });

  const csvColumns = useMemo(
    () => [
      csvColumn<TcmEncounter>("Patient Name", e => e.patientName),
      csvColumn<TcmEncounter>("Patient DOB", e => e.patientDateOfBirth),
      csvColumn<TcmEncounter>("Facility", e => e.facilityName),
      csvColumn<TcmEncounter>("Latest Event", e => e.latestEvent),
      csvColumn<TcmEncounter>("Class", e => e.class),
      csvColumn<TcmEncounter>("Admit Time", e =>
        e.admitTime ? new Date(e.admitTime).toISOString() : ""
      ),
      csvColumn<TcmEncounter>("Discharge Time", e =>
        e.dischargeTime ? new Date(e.dischargeTime).toISOString() : ""
      ),
      csvColumn<TcmEncounter>("Outreach Status", e => e.outreachStatus),
      csvColumn<TcmEncounter>("Last Outreach Date", e =>
        e.lastOutreachDate ? new Date(e.lastOutreachDate).toISOString() : ""
      ),
      csvColumn<TcmEncounter>("Note", e => e.freetextNote ?? ""),
      csvColumn<TcmEncounter>("Phone Numbers", e => e.patientPhoneNumbers.join("; ")),
    ],
    []
  );

  const handleExportToCSV = useCallback(async () => {
    try {
      setIsLoading(true);

      // Fetch all encounters with current filters (no pagination limit)
      const allEncounters: TcmEncounter[] = [];
      let nextPageUrl: string | undefined = undefined;
      let hasMore = true;

      while (hasMore) {
        const response: TcmEncounterResponse | undefined = await getTcmEncountersWithQueryParams(
          nextPageUrl,
          tcmFilters
        );
        if (response?.items) {
          allEncounters.push(...response.items);
        }
        nextPageUrl = response?.meta?.nextPage;
        hasMore = !!nextPageUrl;
      }

      if (allEncounters.length === 0) {
        toast.warning({ title: "No TCM encounters to export" });
        return;
      }

      const count = exportCSV(allEncounters, csvColumns);
      toast.success({ title: `Exported ${count} encounters` });
    } catch (error) {
      capture?.error("Error exporting TCM encounters to CSV", {
        extra: { context: "tcm-encounters.export", error },
      });
      toast.error({ title: "Error exporting TCM encounters to CSV" });
    } finally {
      setIsLoading(false);
    }
  }, [tcmFilters, getTcmEncountersWithQueryParams, toast, exportCSV, csvColumns, capture]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const element = document.getElementById("table-container");
    if (element) {
      setTableFromTopHeight(element.getBoundingClientRect().top);
    }
  }, []);

  useEffect(() => {
    onQueryStateChange?.(effectiveQueryState);
  }, [effectiveQueryState, onQueryStateChange]);

  const handleTypeFilterFromSort = useCallback((eventType: "Admitted" | "Discharged" | "") => {
    dispatch({ type: "syncTypeFromSort", value: eventType });
  }, []);

  const handleSortChange = useCallback((sortState?: TocSortState) => {
    dispatch({ type: "setSort", value: sortState });
  }, []);

  const handleRowsLoaded = useCallback(
    (rows: TcmEncounter[]) => {
      if (!selectedEncounterId) {
        return;
      }

      const matchingRow = rows.find(row => row.id === selectedEncounterId);
      if (!matchingRow) {
        return;
      }

      setSelectedRow(matchingRow);
      setShowSidePanel(true);
    },
    [selectedEncounterId]
  );
  const handleRowsLoadedRef = useRef(handleRowsLoaded);
  useEffect(() => {
    handleRowsLoadedRef.current = handleRowsLoaded;
  }, [handleRowsLoaded]);

  function search() {
    const normalizedSearch = filters.searchText.trim() || undefined;
    dispatch({ type: "applySearch" });
    gridRef?.current?.resetSearch(createFilters({ search: normalizedSearch }));
  }

  function clearFilters() {
    dispatch({ type: "clearFilters" });
    gridRef?.current?.api?.applyColumnState({ defaultState: { sort: null } });
    gridRef?.current?.resetSearch({});
  }

  const onRowClick = useCallback((row: TcmEncounter) => {
    setSelectedRow(row);
    setSelectedEncounterId(row.id);
    setShowSidePanel(true);
  }, []);

  const handleCloseSidePanel = useCallback(() => {
    setShowSidePanel(false);
    setSelectedRow(null);
    setSelectedEncounterId(undefined);
  }, []);

  return (
    <Flex p={10}>
      <Box px={5} flexGrow={1}>
        <Flex alignItems={"center"} justifyContent={"space-between"}>
          <Heading size="xl">Patient Transitions of Care</Heading>
        </Flex>
        <Divider my={6} />
        <Flex
          mb={5}
          align={{ base: "stretch", md: "center" }}
          justify="space-between"
          flexWrap="nowrap"
          gap={4}
        >
          <InputGroup>
            <InputLeftElement pointerEvents="none" color="gray.300" fontSize="1.2em">
              <SearchIcon />
            </InputLeftElement>
            <Input
              minW={"300px"}
              w={"35%"}
              value={filters.searchText}
              placeholder="Search encounters"
              onClick={() => Analytics.emit(Actions.search, Features.facility)}
              onChange={e => dispatch({ type: "setSearchText", value: e.target.value })}
              onKeyDown={key => {
                if (enterKeys.includes(key.code)) search();
              }}
              _hover={{ borderColor: METRIPORT_PRIMARY }}
              _active={{ borderColor: METRIPORT_PRIMARY }}
              _focus={{ borderColor: METRIPORT_PRIMARY }}
            />
            <Button onClick={search} ml={2}>
              Search
            </Button>
            <Button onClick={clearFilters} ml={2}>
              Clear
            </Button>
          </InputGroup>
          <Flex gap={4}>
            <FilterBar
              facilitiesQuery={facilitiesQuery}
              cohortsQuery={cohortsQuery}
              selectedFacility={filters.facility}
              selectedCohort={filters.cohort}
              selectedType={filters.type}
              selectedDays={filters.days}
              selectedCoding={filters.coding}
              selectedStatus={filters.status}
              selectedClass={filters.class}
              handleFacilitySelect={handleFacilitySelect}
              handleCohortSelect={handleCohortSelect}
              handleTypeSelect={handleTypeSelect}
              handleDaySelect={handleDaySelect}
              handleCodingSelect={handleCodingSelect}
              handleStatusSelect={handleStatusSelect}
              handleClassSelect={handleClassSelect}
            />
          </Flex>
          <Button onClick={handleExportToCSV} isLoading={isLoading} size="sm" flexShrink={0}>
            Export CSV
          </Button>
        </Flex>
        <Box
          id="table-container"
          style={{ width: "100%", height: `calc(95vh - ${tableFromTop}px)` }}
        >
          <TransitionsOfCareGrid
            ref={gridRef}
            getTcmEncounters={getTcmEncountersWithQueryParams}
            initialFilters={tcmFilters}
            initialSort={filters.sort}
            isLoading={isLoading}
            numItemsPerPage={GET_TCM_ENCOUNTER_DEFAULT_COUNT}
            onRowClick={onRowClick}
            onRowsLoadedRef={handleRowsLoadedRef}
            onSortChange={handleSortChange}
            setEventTypeFilter={handleTypeFilterFromSort}
            onNoteUpdate={(encounterId, note) => handleNoteUpdate(encounterId, note, false)}
          />
        </Box>
        <Text align={"right"} pt={3}>
          Total amount of TCM encounters: {totalCountOfTcmEncounters}
        </Text>
      </Box>
      <SidePanel showPanel={showSidePanel} setSidePanelContent={handleCloseSidePanel}>
        {selectedRow && (
          <EncounterDetails
            encounter={selectedRow}
            onNoteChange={note => selectedRow.id && handleNoteUpdate(selectedRow.id, note, true)}
            onSummaryFilePathChange={summaryFilePath =>
              selectedRow.id && onSummaryFilePathChange(selectedRow.id, summaryFilePath)
            }
            onOutreachLogUpdate={handleOutreachLogUpdate}
            onNavigateToPatient={onNavigateToPatient}
            getPatientUrl={getPatientUrl}
            isImpersonating={isImpersonating}
          />
        )}
      </SidePanel>
    </Flex>
  );
}

export default TransitionsOfCareContent;

// Re-export types and utilities
export type { TcmEncounter, TcmEncounterResponse } from "../../../api/tcm-encounter";
export type {
  GetTcmEncounterQueryParams,
  TocQueryState,
  TocSortDirection,
  TocSortField,
  TocSortState,
} from "./filters";
export { createTcmApi, GET_TCM_ENCOUNTER_DEFAULT_COUNT } from "./api";
export type { OutreachAction } from "./outreach-actions";
export { EncounterDetails } from "../encounter/encounter-details";
export type { EncounterDetailsProps } from "../encounter/encounter-details";
